import { Button, Card, Col } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SolarIcon from 'components/icons/SolarIcon';
import SpinnerBlock from 'components/others/SpinnerBlock';
import { useCatalogDashboard } from './model';

const CatalogDashboard = () => {
    const { t } = useTranslation('Dashboard');
    const { enabled, products, sets, services, warranties } = useCatalogDashboard();
    if (!enabled) return null;
    const sources = [
        { key: 'products', to: '/products', source: products, variant: 'primary', icon: 'box-seam' },
        { key: 'sets', to: '/sets', source: sets, variant: 'info', icon: 'collection' },
        { key: 'services', to: '/services', source: services, variant: 'success', icon: 'toolbox' },
    ];

    return (
        <Col xs={12} xl={6} className="d-flex">
            <Card className="w-100 dashboard-summary-panel mb-0">
                <Card.Header className="d-flex align-items-center gap-3">
                    <span className="avtar avtar-s bg-light-success text-success rounded" aria-hidden="true">
                        <SolarIcon name="box-seam" />
                    </span>
                    <h5 className="mb-0">{t('panels.catalogTitle')}</h5>
                </Card.Header>
                <Card.Body className="d-flex flex-column">
                    <div className="list-group list-group-flush mb-4">
                        {sources.map(({ key, to, source, variant, icon }) => (
                            <div key={key} className="list-group-item px-0 py-3 position-relative" aria-busy={source.loading}>
                                <SpinnerBlock show={source.loading} />
                                <Link to={to} className="d-flex align-items-center gap-3 text-decoration-none text-body text-hover-primary">
                                    <span className={`avtar bg-light-${variant} text-${variant} rounded flex-shrink-0`} aria-hidden="true">
                                        <SolarIcon name={icon} />
                                    </span>
                                    <span className="flex-grow-1">
                                        <span className="d-block fw-medium">{t(`panels.${key}`)}</span>
                                        <span className="text-muted f-12">{t('panels.openRegistry')}</span>
                                    </span>
                                    <strong className="dashboard-summary-panel__number text-body">{source.data?.total ?? '—'}</strong>
                                    <span className="avtar avtar-xs rounded-circle bg-body-tertiary text-muted flex-shrink-0" aria-hidden="true">
                                        <SolarIcon name="arrow-right" />
                                    </span>
                                </Link>
                                {!source.loading && !source.data && (
                                    <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mt-2" role="status">
                                        <span className="text-danger">{t('panels.unavailable')}</span>
                                        <Button size="sm" variant="outline-primary" onClick={source.retry}>{t('panels.retry')}</Button>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                    <div className="bg-body-tertiary rounded p-3 mt-auto position-relative" aria-busy={warranties.loading}>
                        <SpinnerBlock show={warranties.loading} />
                        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
                            <h6 className="mb-0 d-flex align-items-center gap-2">
                                <span className="avtar avtar-xs bg-light-warning text-warning rounded-circle" aria-hidden="true"><SolarIcon name="check-circle" /></span>
                                {t('panels.warranties')}
                            </h6>
                            <Link to="/warranties" className="dashboard-summary-panel__link text-primary">{t('panels.openRegistry')}<SolarIcon name="arrow-right" /></Link>
                        </div>
                        <dl className="dashboard-summary-panel__metrics mb-0">
                            {([
                                { key: 'total', variant: 'primary' },
                                { key: 'valid', variant: 'success' },
                                { key: 'expired', variant: 'danger' },
                            ] as const).map(({ key, variant }) => (
                                <div key={key} className="dashboard-summary-panel__metric">
                                    <dt className="text-muted fw-normal f-12 mb-1">{t(`panels.${key}`)}</dt>
                                    <dd className={`h4 mb-0 text-${variant}`}>
                                        {warranties.data?.[key] ?? '—'}
                                    </dd>
                                </div>
                            ))}
                        </dl>
                        {!warranties.loading && !warranties.data && (
                            <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mt-3" role="status">
                                <span className="text-danger">{t('panels.unavailable')}</span>
                                <Button size="sm" variant="outline-primary" onClick={warranties.retry}>{t('panels.retry')}</Button>
                            </div>
                        )}
                    </div>
                </Card.Body>
            </Card>
        </Col>
    );
};

export default CatalogDashboard;
